import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
    BookOpen, Facebook, Instagram, Linkedin, Mail, MapPin, MessageCircle,
    Phone, Send, Stethoscope, Twitter, Youtube,
} from "lucide-react";
import { toast } from "sonner";
import { subscribeNewsletter } from "../lib/api";
import Logo from "./Logo";
import { ButtonRing } from "./LoadingRing";
import { useGlobals, usePublishedPages } from "../site/useGlobals";
import { spacingStyle } from "../site/spacing";
import { useSeoSettings } from "../lib/useSeoSettings";

const SOCIAL_ICONS = {
    instagram: Instagram, facebook: Facebook, twitter: Twitter, x: Twitter, youtube: Youtube,
    linkedin: Linkedin, whatsapp: MessageCircle, practo: Stethoscope, justdial: Phone,
    wikipedia: BookOpen, google_maps: MapPin,
};
const SOCIAL_LABELS = {
    instagram: "Instagram", facebook: "Facebook", twitter: "X", x: "X", youtube: "YouTube",
    linkedin: "LinkedIn", whatsapp: "WhatsApp", practo: "Practo", justdial: "JustDial",
    wikipedia: "Wikipedia", google_maps: "Find us on Google",
};

const SPANS = { third: "md:col-span-4", half: "md:col-span-6", two_thirds: "md:col-span-8", full: "md:col-span-12" };

/** Staff-added footer sections — plain text, an image, or both, from CRM → Website → Footer. */
const FooterBlock = ({ b }) => {
    if (b.visible === false) return null;
    const href = b.href || "";
    const external = /^https?:\/\//i.test(href);
    const wrap = (node) => (href
        ? (external
            ? <a href={href} target="_blank" rel="noreferrer" data-testid={`footer-block-link-${b.id}`}>{node}</a>
            : <Link to={href} data-testid={`footer-block-link-${b.id}`}>{node}</Link>)
        : node);

    return (
        <div className={SPANS[b.width] || SPANS.third} data-testid={`footer-block-${b.id}`}>
            {b.title && (
                <div className="text-xs uppercase tracking-[0.25em] text-zinc-500 mb-4">{b.title}</div>
            )}
            {b.image_url && wrap(
                <img src={b.image_url} alt={b.image_alt || b.title || ""} loading="lazy"
                    className="max-h-28 w-auto object-contain" />,
            )}
            {b.text && (
                <p className={`text-sm text-zinc-400 leading-relaxed whitespace-pre-line ${b.image_url ? "mt-4" : ""}`}>
                    {b.text}
                </p>
            )}
            {href && !b.image_url && (
                <div className="mt-3">
                    {wrap(<span className="mono text-[10px] tracking-[0.18em] text-lime hover:underline">
                        {(b.link_label || "Learn more").toUpperCase()}
                    </span>)}
                </div>
            )}
        </div>
    );
};

export default function Footer() {
    const { footer: f } = useGlobals();
    const pages = usePublishedPages();
    const brand = useSeoSettings();
    // Brand links live in CRM → SEO & Google so Google and visitors see exactly the same profiles.
    const brandLinks = Object.entries(brand.profiles || {})
        .filter(([, href]) => href)
        .map(([platform, href]) => ({ id: platform, platform, href, label: SOCIAL_LABELS[platform] || platform }));
    const socials = brandLinks.length ? brandLinks : (f.socials || []);
    const [email, setEmail] = useState("");
    const [loading, setLoading] = useState(false);

    if (f.visible === false) return null;
    const extraLinks = pages
        .filter((p) => p.show_in_menu && ["footer", "both"].includes(p.menu_location || "header"))
        .map((p) => ({ id: `page_${p.slug}`, label: p.name, href: `/${p.slug}` }));
    const columns = [
        ...(f.columns || []).filter((c) => c.visible !== false),
        ...(extraLinks.length ? [{ id: "f_pages", title: "Explore", links: extraLinks }] : []),
    ];
    const blocks = (f.blocks || []).filter((b) => b.visible !== false);

    const onSubscribe = async (e) => {
        e.preventDefault();
        if (!email) return;
        setLoading(true);
        try {
            await subscribeNewsletter(email);
            toast.success("Welcome to the inner circle!");
            setEmail("");
        } catch (err) {
            const detail = err?.response?.data?.detail;
            toast.error(typeof detail === "string" ? detail
                : Array.isArray(detail) ? "Please enter a valid email address"
                : "Couldn't subscribe. Try again.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <footer className="relative mt-20 border-t border-white/10 bg-bg0" data-testid="site-footer"
            style={spacingStyle(f)}>
            <div className="mx-auto max-w-7xl px-6 md:px-12 py-12 md:py-14">
                <div className="grid md:grid-cols-12 gap-10">
                    <div className="md:col-span-5">
                        <div className="mb-5">
                            {f.logo_url ? <img src={f.logo_url} alt="ReHealthMe" className="h-11 w-auto" /> : <Logo className="h-11 w-auto" />}
                            {f.tagline && <div className="mt-3 text-[10px] tracking-[0.3em] text-zinc-500 uppercase">{f.tagline}</div>}
                        </div>
                        {f.legal_name && (
                            <div className="mb-3 font-display text-lg md:text-xl uppercase tracking-tight text-white"
                                data-testid="footer-legal-name">{f.legal_name}</div>
                        )}
                        <p className="text-zinc-400 max-w-md leading-relaxed">{f.about}</p>

                        {f.newsletter_enabled !== false && (
                            <form onSubmit={onSubscribe} className="mt-6 flex gap-2 max-w-md" data-testid="newsletter-form">
                                <div className="relative flex-1">
                                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
                                    <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                                        placeholder={f.newsletter_placeholder || "you@strong.com"}
                                        data-testid="newsletter-email-input"
                                        className="w-full bg-bg1 border border-white/10 text-white pl-10 pr-4 py-3 rounded-none focus:border-lime focus:ring-1 focus:ring-lime outline-none transition-all placeholder:text-zinc-600 text-sm" />
                                </div>
                                <button type="submit" disabled={loading} data-testid="newsletter-submit-button"
                                    className="btn-pill btn-primary !py-3 !px-5">
                                    {loading ? <ButtonRing /> : <Send className="w-4 h-4" />}
                                </button>
                            </form>
                        )}
                    </div>

                    {columns.map((col) => (
                        <div key={col.id} className="md:col-span-2" data-testid={`footer-column-${col.id}`}>
                            <div className="text-xs uppercase tracking-[0.25em] text-zinc-500 mb-4">{col.title}</div>
                            <ul className="space-y-2 text-zinc-300">
                                {(col.links || []).map((l) => (
                                    <li key={l.id}>
                                        <Link to={l.href || "/"} className="hover:text-lime" data-testid={`footer-link-${l.id}`}>{l.label}</Link>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    ))}

                    <div className="md:col-span-3">
                        <div className="text-xs uppercase tracking-[0.25em] text-zinc-500 mb-4">Connect</div>
                        <div className="space-y-2">
                            {socials.map((s) => {
                                const Icon = SOCIAL_ICONS[s.platform] || Instagram;
                                return (
                                    <a key={s.id} href={s.href} target="_blank" rel="noreferrer" data-testid={`footer-social-${s.id}`}
                                        className="inline-flex items-center gap-2 text-zinc-300 hover:text-lime">
                                        <Icon className="w-4 h-4" /> {s.label || s.platform}
                                    </a>
                                );
                            })}
                        </div>
                        {f.contact_email && <div className="mt-4 text-xs text-zinc-500">{f.contact_email}</div>}
                        {f.contact_phone && (
                            <div className="mt-1 text-xs text-zinc-500 inline-flex items-center gap-1.5">
                                <Phone className="w-3 h-3" /> {f.contact_phone}
                            </div>
                        )}
                        {f.contact_address && <div className="mt-1 text-xs text-zinc-500">{f.contact_address}</div>}
                        {(f.badges || []).length > 0 && (
                            <div className="mt-5 flex flex-wrap gap-2">
                                {f.badges.map((b) => (
                                    <span key={b} className="mono text-[9px] tracking-[0.14em] text-zinc-400 border border-white/10 px-2 py-1">{b}</span>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                {blocks.length > 0 && (
                    <div className="mt-12 pt-10 border-t border-white/5 grid md:grid-cols-12 gap-8 md:gap-10"
                        data-testid="footer-blocks">
                        {blocks.map((b) => <FooterBlock key={b.id} b={b} />)}
                    </div>
                )}

                {f.wordmark !== "" && (
                    <div className="mt-14 pt-8 border-t border-white/5">
                        <div className="font-display text-[12vw] md:text-[10vw] leading-none tracking-tighter text-white/[0.04] select-none uppercase">
                            {f.wordmark || "Rehealthme"}
                        </div>
                    </div>
                )}
                <div className="mt-2 text-xs text-zinc-500 flex items-center justify-between flex-wrap gap-4">
                    <div>{(f.copyright || "© {year} Rehealthme. All rights reserved.").replace("{year}", new Date().getFullYear())}</div>
                    <div className="flex items-center gap-4">
                        {(f.legal_links || []).map((l, i) => (
                            <Link key={i} to={l.href || "/"} className="hover:text-lime" data-testid={`footer-legal-${i}`}>{l.label}</Link>
                        ))}
                        {f.note && <span>{f.note}</span>}
                    </div>
                </div>
            </div>
        </footer>
    );
}
